import { MesfModal } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Button,
	Divider,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type {
	ProductNode,
	StraightenerProduct,
} from "../../straightener-recipes/models/StraightenerRecipe";
import { useStartStraightenerCampaign } from "../hooks/useStraightenerTrend";

interface Props {
	open: boolean;
	products: StraightenerProduct[];
	// Products with their recipes, to say which recipe the new product brings.
	nodes: ProductNode[];
	// The product on the straightener now; it cannot be started again.
	currentProductId: number | null;
	currentProductLabel: string | null;
	onChanged: () => void;
	onError: (message: string) => void;
	onClose: () => void;
}

const productLabel = (p: StraightenerProduct) =>
	`(${p.ProductTypeName}) ${p.Name}`;

// Product Change for the straightener, the same idea as the Mill Setup's: the
// operator says which product the line is set up for, and that starts a new
// straightener campaign. Nothing is preselected: the product on the line is the
// one being replaced.
export const ProductChangeModal = ({
	open,
	products,
	nodes,
	currentProductId,
	currentProductLabel,
	onChanged,
	onError,
	onClose,
}: Props) => {
	const [productType, setProductType] = useState<string | null>(null);
	const [product, setProduct] = useState<StraightenerProduct | null>(null);
	const [scheduledBars, setScheduledBars] = useState("");

	useEffect(() => {
		if (!open) return;
		setProductType(null);
		setProduct(null);
		setScheduledBars("");
	}, [open]);

	const { startCampaign, isStarting } = useStartStraightenerCampaign({
		onSuccess: onChanged,
		onError: (e) => onError(e.message),
	});

	const productTypes = useMemo(
		() =>
			[...new Set(products.map((p) => p.ProductTypeName))]
				.filter(Boolean)
				.sort((a, b) => a.localeCompare(b)),
		[products],
	);

	const options = useMemo(
		() =>
			products
				.filter((p) => p.Id !== currentProductId)
				.filter((p) => !productType || p.ProductTypeName === productType)
				.sort((a, b) => productLabel(a).localeCompare(productLabel(b))),
		[products, currentProductId, productType],
	);

	const defaultRecipe = product
		? (nodes.find((n) => n.productSizeId === product.Id)?.current ?? null)
		: null;

	const bars = scheduledBars.trim() === "" ? null : Number(scheduledBars);
	const barsInvalid = bars != null && (!Number.isInteger(bars) || bars < 0);

	const confirm = () => {
		if (!product || barsInvalid) return;
		startCampaign({ productSizeId: product.Id, scheduledBarsCount: bars });
	};

	return (
		<MesfModal
			id="straightener-product-change"
			title="Product change"
			open={open}
			handleClose={onClose}
			maxWidth="sm"
		>
			<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
				<Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
					On the straightener now:{" "}
					<strong>{currentProductLabel ?? "no product selected"}</strong>
				</Typography>

				<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
					Product
				</Typography>
				<Divider sx={{ mb: 1.5 }} />
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Autocomplete
						size="small"
						options={productTypes}
						value={productType}
						onChange={(_, v) => {
							setProductType(v);
							if (v && product && product.ProductTypeName !== v)
								setProduct(null);
						}}
						renderInput={(params) => (
							<TextField {...params} label="Product Type" placeholder="All" />
						)}
					/>
					<Autocomplete
						size="small"
						options={options}
						value={product}
						onChange={(_, v) => setProduct(v)}
						getOptionLabel={productLabel}
						isOptionEqualToValue={(a, b) => a.Id === b.Id}
						autoHighlight
						openOnFocus
						noOptionsText="No products"
						renderInput={(params) => (
							<TextField {...params} label="Product" required />
						)}
					/>
					{product ? (
						<Typography variant="body2" sx={{ color: "text.secondary" }}>
							Recipe:{" "}
							{defaultRecipe ? (
								<strong>
									{`${defaultRecipe.Name ?? "(unnamed)"} · v${defaultRecipe.Version}`}
								</strong>
							) : (
								"this product has no default recipe"
							)}
						</Typography>
					) : null}
				</Box>

				<Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>
					Production Schedule
				</Typography>
				<Divider sx={{ mb: 1.5 }} />
				<TextField
					size="small"
					label="Scheduled Bars"
					type="number"
					value={scheduledBars}
					onChange={(e) => setScheduledBars(e.target.value)}
					error={barsInvalid}
					helperText={barsInvalid ? "Whole number, 0 or more" : " "}
					slotProps={{ htmlInput: { min: 0, step: 1 } }}
					fullWidth
				/>
			</MesfModal.Content>
			<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 2,
						width: "100%",
					}}
				>
					<Button variant="contained" color="inherit" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={!product || barsInvalid || isStarting}
						onClick={confirm}
					>
						Change product
					</Button>
				</Box>
			</MesfModal.Actions>
		</MesfModal>
	);
};
